'use client';

import { useState } from 'react';
import paymentService from '@/services/PaymentService';
import { showPaymentTestCardUi } from '@/lib/payment-dev-tools';
import { GARANTI_TEST_CARDS } from '@/lib/paymentTestCards';

export interface PaymentCardFormValue {
  cardHolder: string;
  cardNumber: string;
  expireMonth: string;
  expireYear: string;
  cvc: string;
  use3D: boolean;
}

export interface InstallmentOption {
  installmentNumber: number;
  installmentPrice: number;
  totalPrice: number;
}

export interface PaymentCardFormProps {
  value: PaymentCardFormValue;
  onChange: (next: Partial<PaymentCardFormValue>) => void;
  /** Çeviri: (key, fallback) => string */
  t: (key: string, fallback: string) => string;
  /** Test modu: banner + test kart dropdown + Load Test Card */
  testMode?: boolean;
  /** garanti_pos ise dropdown kullanılır; diğerlerinde Load Test Card butonu */
  paymentProviderKey?: string;
  /** Test kartı yükle (iyzico vb. için rastgele kart) */
  onLoadTestCard?: () => void;
  /** Kart animasyonu (ön/arka) göster */
  showCardAnimation?: boolean;
  /** Taksit seçenekleri (InvoicePayment) */
  installments?: InstallmentOption[];
  installment?: number;
  onInstallmentChange?: (n: number) => void;
  currencySymbol?: string;
  /** Form altında ek ipucu */
  cardHint?: string;
  /** POS banka taksiti başlığı (ödeme takviminden ayrı) */
  installmentSectionTitle?: string;
  fieldErrors?: {
    card_holder?: string;
    card_number?: string;
    card_expiry?: string;
    card_cvc?: string;
  };
}

function fieldErrorClass(hasError: boolean): string {
  return hasError
    ? 'border-red-500 focus:!border-red-500 ring-1 ring-red-200'
    : 'border-[rgba(0,0,0,0.08)] focus:!border-[rgba(0,0,0,0.40)]';
}

export default function PaymentCardForm({
  value,
  onChange,
  t,
  testMode = false,
  paymentProviderKey,
  onLoadTestCard,
  showCardAnimation = true,
  installments = [],
  installment = 1,
  onInstallmentChange,
  currencySymbol = '₺',
  cardHint,
  installmentSectionTitle,
  fieldErrors,
}: PaymentCardFormProps) {
  const [isFlipped, setIsFlipped] = useState(false);
  const { cardHolder, cardNumber, expireMonth, expireYear, cvc, use3D } = value;
  const getCardType = (num: string) => paymentService.getCardType(num);

  const showTestCardBlock = showPaymentTestCardUi(testMode);
  const isIyzico = paymentProviderKey === 'iyzico' || paymentProviderKey === 'iyzico_checkout';
  const isGaranti =
    paymentProviderKey === 'garanti_pos' ||
    paymentProviderKey === 'garanti';

  return (
    <div className="space-y-6">
      {/* Test Mode Active banner – sadece backend test_mode true ise */}
      {testMode && (
        <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
          <div className="flex items-start">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-yellow-400" fill="currentColor" viewBox="0 0 20 20" aria-hidden>
                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3 flex-1">
              <h3 className="text-sm font-medium text-yellow-800">{t('payment.test_mode.title', 'Test Mode Active')}</h3>
              <p className="mt-1 text-sm text-yellow-700">
                {t('payment.test_mode.description', 'Test card information has been loaded automatically. No real payment will be made.')}
              </p>
            </div>
            {!cardNumber && !isGaranti && onLoadTestCard && (
              <button type="button" onClick={onLoadTestCard} className="ml-3 text-sm font-medium text-yellow-800 hover:text-yellow-900 underline">
                {t('payment.test_mode.load_card', 'Load Test Card')}
              </button>
            )}
          </div>
        </div>
      )}

      {showTestCardBlock && (
        <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg space-y-2">
          <label className="block text-sm font-medium text-amber-900">{t('payment.test_card.select', 'Select test card')}</label>
          {isGaranti ? (
            <>
              <select
                className="w-full border border-amber-300 rounded-lg px-3 py-2 text-sm text-amber-900 bg-white"
                value=""
                onChange={(e) => {
                  const idx = e.target.selectedIndex;
                  if (idx <= 0) return;
                  const card = GARANTI_TEST_CARDS[idx - 1];
                  if (card) {
                    onChange({
                      cardNumber: card.number,
                      expireMonth: card.month,
                      expireYear: card.year,
                      cvc: card.cvc,
                      cardHolder: 'TEST KART',
                    });
                  }
                }}
              >
                <option value="">— {t('payment.test_card.select', 'Select test card')} —</option>
                {GARANTI_TEST_CARDS.map((card, i) => (
                  <option key={i} value={card.number}>
                    {card.label} •••• {card.number.slice(-4)}
                  </option>
                ))}
              </select>
              <p className="text-xs text-amber-700">{t('payment.test_card.otp_hint', '3D OTP code: 1 4 7 8 5 2')}</p>
              <p className="text-xs text-green-700 mt-1">
                {t('payment.test_card.success_hint', 'For successful payment: select Simülatör, AMEX or SFAMEX card. 3D OTP: 147852')}
              </p>
            </>
          ) : (
            <>
              {onLoadTestCard && (
                <button
                  type="button"
                  onClick={onLoadTestCard}
                  className="w-full sm:w-auto px-4 py-2 text-sm font-medium text-amber-900 bg-amber-200 hover:bg-amber-300 rounded-lg border border-amber-400"
                >
                  {t('payment.test_mode.load_card', 'Load Test Card')}
                </button>
              )}
            </>
          )}
        </div>
      )}

      {cardHint && <p className="text-xs text-gray-600 mb-2">{cardHint}</p>}

      {showCardAnimation && (
        <div className="flex justify-center">
          <div className="relative h-56 w-full max-w-md">
            <div
              className="relative w-full h-full transition-transform duration-700"
              style={{ transformStyle: 'preserve-3d', transform: isFlipped ? 'rotateY(180deg)' : 'rotateY(0deg)' }}
            >
              <div
                className={`absolute inset-0 rounded-xl shadow-2xl p-6 text-white bg-gradient-to-br ${
                  getCardType(cardNumber) === 'visa' ? 'from-blue-600 to-blue-800' : getCardType(cardNumber) === 'mastercard' ? 'from-orange-500 to-red-600' : 'from-indigo-600 to-purple-800'
                }`}
                style={{ backfaceVisibility: 'hidden', WebkitBackfaceVisibility: 'hidden', transform: 'rotateY(0deg)' }}
              >
                <div className="absolute top-6 left-6 w-12 h-10 bg-yellow-400 rounded-md flex items-center justify-center">
                  <div className="w-8 h-6 bg-gradient-to-br from-yellow-300 to-yellow-500 rounded-sm border border-yellow-600" />
                </div>
                <div className="absolute bottom-20 left-6 right-6">
                  <div className="text-2xl font-mono tracking-wider">{cardNumber || '•••• •••• •••• ••••'}</div>
                </div>
                <div className="absolute bottom-6 left-6 right-6 flex justify-between items-end">
                  <div>
                    <div className="text-xs text-white/80 mb-1">{t('payment.card_holder', 'CARD HOLDER')}</div>
                    <div className="text-sm font-semibold uppercase">{cardHolder || 'AD SOYAD'}</div>
                  </div>
                  <div className="text-right">
                    <div className="text-xs text-white/80 mb-1">SKT</div>
                    <div className="text-sm font-semibold">{expireMonth && expireYear ? `${expireMonth}/${expireYear}` : 'MM/YY'}</div>
                  </div>
                </div>
              </div>
              <div
                className={`absolute inset-0 rounded-xl shadow-2xl p-6 text-white bg-gradient-to-br ${
                  getCardType(cardNumber) === 'visa' ? 'from-blue-700 to-blue-900' : getCardType(cardNumber) === 'mastercard' ? 'from-orange-600 to-red-700' : 'from-indigo-700 to-purple-900'
                }`}
                style={{ backfaceVisibility: 'hidden', WebkitBackfaceVisibility: 'hidden', transform: 'rotateY(180deg)' }}
              >
                <div className="absolute top-6 left-0 right-0 h-12 bg-black" />
                <div className="absolute bottom-16 right-6">
                  <div className="bg-white px-4 py-2 rounded text-black font-mono font-bold">{cvc || '•••'}</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      <div>
        <label className="block font-semibold text-base sm:text-lg text-alsero-dark-gray mb-2">{t('payment.card_holder', 'Card Holder Name')}</label>
        <input
          type="text"
          value={cardHolder}
          onChange={(e) => onChange({ cardHolder: e.target.value.toUpperCase() })}
          className={`border transition-all ease-in-out duration-300 rounded-lg text-[#4F545A] font-medium text-sm sm:text-[15px] px-4 sm:px-6 py-3 sm:py-4 w-full ${fieldErrorClass(!!fieldErrors?.card_holder)}`}
          placeholder={t('payment.card_holder_placeholder', 'NAME SURNAME')}
          required
          autoComplete="cc-name"
        />
        {fieldErrors?.card_holder && <p className="mt-1 text-xs text-red-600">{fieldErrors.card_holder}</p>}
      </div>
      <div>
        <label className="block font-semibold text-base sm:text-lg text-alsero-dark-gray mb-2">{t('payment.card_number', 'Card Number')}</label>
        <input
          type="text"
          inputMode="numeric"
          value={cardNumber}
          onChange={(e) => onChange({ cardNumber: paymentService.formatCardNumber(e.target.value) })}
          className={`border transition-all ease-in-out duration-300 rounded-lg text-[#4F545A] font-medium text-sm sm:text-[15px] px-4 sm:px-6 py-3 sm:py-4 w-full ${fieldErrorClass(!!fieldErrors?.card_number)}`}
          placeholder="0000 0000 0000 0000"
          maxLength={19}
          required
          autoComplete="cc-number"
        />
        {fieldErrors?.card_number && <p className="mt-1 text-xs text-red-600">{fieldErrors.card_number}</p>}
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div>
          <label className="block font-semibold text-base sm:text-lg text-alsero-dark-gray mb-2">{t('payment.month', 'Month')}</label>
          <select
            value={expireMonth}
            onChange={(e) => onChange({ expireMonth: e.target.value })}
            className={`border rounded-lg text-[#4F545A] font-medium text-sm sm:text-[15px] px-4 sm:px-6 py-3 sm:py-4 w-full ${fieldErrorClass(!!fieldErrors?.card_expiry)}`}
            required
            autoComplete="cc-exp-month"
          >
            <option value="">{t('payment.month', 'Month')}</option>
            {Array.from({ length: 12 }, (_, i) => (
              <option key={i + 1} value={String(i + 1).padStart(2, '0')}>{String(i + 1).padStart(2, '0')}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="block font-semibold text-base sm:text-lg text-alsero-dark-gray mb-2">{t('payment.year', 'Year')}</label>
          <select
            value={expireYear}
            onChange={(e) => onChange({ expireYear: e.target.value })}
            className={`border rounded-lg text-[#4F545A] font-medium text-sm sm:text-[15px] px-4 sm:px-6 py-3 sm:py-4 w-full ${fieldErrorClass(!!fieldErrors?.card_expiry)}`}
            required
            autoComplete="cc-exp-year"
          >
            <option value="">{t('payment.year', 'Year')}</option>
            {Array.from({ length: 10 }, (_, i) => {
              const year = new Date().getFullYear() + i;
              return <option key={year} value={String(year).slice(-2)}>{year}</option>;
            })}
          </select>
        </div>
        <div>
          <label className="block font-semibold text-base sm:text-lg text-alsero-dark-gray mb-2">{t('payment.cvc', 'CVC')}</label>
          <input
            type="text"
            value={cvc}
            onChange={(e) => onChange({ cvc: e.target.value.replace(/\D/g, '').slice(0, 4) })}
            onFocus={() => setIsFlipped(true)}
            onBlur={() => setIsFlipped(false)}
            className={`border rounded-lg text-[#4F545A] font-medium text-sm sm:text-[15px] px-4 sm:px-6 py-3 sm:py-4 w-full ${fieldErrorClass(!!fieldErrors?.card_cvc)}`}
            placeholder="000"
            maxLength={4}
            required
            autoComplete="cc-csc"
          />
          {fieldErrors?.card_cvc && <p className="mt-1 text-xs text-red-600">{fieldErrors.card_cvc}</p>}
        </div>
      </div>
      {fieldErrors?.card_expiry && (
        <p className="text-xs text-red-600 -mt-2">{fieldErrors.card_expiry}</p>
      )}

      {installments.length > 1 && onInstallmentChange && (
        <div>
          <label className="block font-semibold text-base sm:text-lg text-alsero-dark-gray mb-3">
            {installmentSectionTitle ?? t('payment.bank_installment', t('payment.installment', 'Installment'))}
          </label>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
            {installments.map((inst) => (
              <button
                key={inst.installmentNumber}
                type="button"
                onClick={() => onInstallmentChange(inst.installmentNumber)}
                className={`relative text-left p-4 rounded-xl border-2 transition-all duration-200 ${
                  installment === inst.installmentNumber ? 'border-emerald-500 bg-emerald-50/80' : 'border-gray-200 bg-white hover:border-gray-300'
                }`}
              >
                {installment === inst.installmentNumber && (
                  <span className="absolute top-2 right-2 flex h-6 w-6 items-center justify-center rounded-full bg-emerald-500 text-white">
                    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>
                  </span>
                )}
                <div className="font-semibold text-[#1a1a1a] text-sm uppercase tracking-wide">
                  {inst.installmentNumber === 1 ? t('payment.single_payment', '') : `${inst.installmentNumber} ${t('payment.installment_short', '')}`}
                </div>
                {inst.installmentNumber === 1 ? (
                  <div className="mt-2 text-lg font-bold text-emerald-700">{inst.totalPrice.toFixed(2)} {currencySymbol}</div>
                ) : (
                  <>
                    <div className="mt-1 text-base font-bold text-gray-800">{inst.installmentPrice.toFixed(2)}</div>
                    <div className="text-xs text-gray-500 mt-0.5">×{inst.installmentNumber}</div>
                    <div className="text-sm font-medium text-gray-600 mt-1">{t('payment.total', '')}: {inst.totalPrice.toFixed(2)} {currencySymbol}</div>
                  </>
                )}
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="flex items-center">
        <input
          type="checkbox"
          id="use3d"
          checked={use3D}
          onChange={(e) => onChange({ use3D: e.target.checked })}
          className="mr-2"
        />
        <label htmlFor="use3d" className="text-sm text-gray-600">{t('payment.3ds', 'Secure payment with 3D Secure')}</label>
      </div>
    </div>
  );
}
